import { useState } from 'react';
import { MonitorCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DeviceActivateForm from './DeviceActivateForm';
import { useAuth } from '../hooks/useAuth';
import { useDevices } from '../hooks/useDevices';
import type { Endpoint } from '../hooks/useEndpoints';

/**
 * Promote an endpoint to a managed device: the one create/activate form, started from what we know
 * of it — its hostname, its address, and the region of the device it is connected to (docs
 * `EndpointNeighbors.md`). Absent for an endpoint that already is a managed device.
 */
export function ManageEndpointButton({ endpoint, size = 'sm' }: { endpoint: Endpoint; size?: 'sm' | 'default' }) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? undefined;
  const { data: devices } = useDevices(organizationId);
  const [open, setOpen] = useState(false);
  if (endpoint.managedDevice || !organizationId) return null;

  const links = endpoint.links ?? [];
  const attachedTo = (links.find((l) => !l.behindDeviceName) ?? links.at(0))?.deviceId;
  const device = devices?.find((d) => d.id === attachedTo);
  const address = endpoint.ipAddresses.find((ip) => !ip.includes(':')) ?? endpoint.ipAddresses.at(0) ?? null;

  return (
    <>
      <Button variant="outline" size={size} onClick={() => { setOpen(true); }}>
        <MonitorCog className="h-3.5 w-3.5" />
        Manage this device
      </Button>
      {open ? (
        <DeviceActivateForm
          organizationId={organizationId}
          initial={{
            name: endpoint.hostname,
            ipAddress: address,
            regionId: device?.regionId ?? null,
            divisionId: device?.divisionId ?? null,
            description: null,
          }}
          onClose={() => { setOpen(false); }}
        />
      ) : null}
    </>
  );
}
